import { useCallback, useState, useMemo, useEffect } from 'react';
import {
  Clipboard,
  Trash2,
  MousePointerClick,
  ZoomIn,
  ZoomOut,
  Maximize,
  Tag,
} from 'lucide-react';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
  ContextMenuShortcut,
} from '@/components/ui/context-menu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
} from '@/components/ui/dropdown-menu';
import { useWorkflowStore } from '@/stores/workflow-store';
import { useCanvasOperations } from '@/hooks/useCanvasOperations';
import { useReactFlow } from 'reactflow';
import { toast } from 'sonner';
import { getShortcuts } from '@/utils/keyboard';

interface CanvasContextMenuProps {
  children: React.ReactNode;
  canvasMenuOpen?: boolean;
  canvasMenuPosition?: { x: number; y: number } | null;
  onCanvasMenuOpenChange?: (open: boolean) => void;
}

export function CanvasContextMenu({ 
  children, 
  canvasMenuOpen = false,
  canvasMenuPosition = null,
  onCanvasMenuOpenChange 
}: CanvasContextMenuProps) {
  const { nodes, setSelectedNode, addNode } = useWorkflowStore();
  const { copyNodes, pasteNodes, deleteSelectedNodes, hasClipboardData } = useCanvasOperations();
  const { fitView, zoomIn, zoomOut, screenToFlowPosition } = useReactFlow();
  
  // Get shortcuts at runtime for proper OS detection
  const shortcuts = useMemo(() => getShortcuts(), []);

  const selectedNodes = nodes.filter((n) => n.selected);
  
  // Track if clipboard has data
  const [canPaste, setCanPaste] = useState(false);

  // Check clipboard status when menu opens or when copy happens
  useEffect(() => {
    const checkClipboard = async () => {
      const hasData = await hasClipboardData();
      setCanPaste(hasData);
    };
    
    // Check immediately
    checkClipboard();
    
    // Also check periodically when menu is open
    if (canvasMenuOpen) {
      const interval = setInterval(checkClipboard, 500);
      return () => clearInterval(interval);
    }
  }, [canvasMenuOpen, hasClipboardData]);

  const handlePaste = useCallback(() => {
    pasteNodes();
  }, [pasteNodes]);

  const handleDeleteSelected = useCallback(() => {
    if (selectedNodes.length > 0) {
      deleteSelectedNodes();
      toast.success(`Deleted ${selectedNodes.length} node(s)`);
    }
  }, [selectedNodes, deleteSelectedNodes]);

  const handleSelectAll = useCallback(() => {
    toast.info('Select all - Coming soon!');
    // TODO: Implement select all
  }, []);

  const handleDeselectAll = useCallback(() => {
    setSelectedNode(null);
    toast.info('Deselected all nodes');
  }, [setSelectedNode]);

  const handleFitView = useCallback(() => {
    fitView({ 
      padding: 0.2, 
      duration: 400,
      includeHiddenNodes: false,
      minZoom: 0.1,
      maxZoom: 1.5,
      nodes: nodes.filter(n => !n.hidden), // Explicitly pass visible nodes
    });
  }, [fitView, nodes]);

  const handleZoomIn = useCallback(() => {
    zoomIn({ duration: 300 });
  }, [zoomIn]);

  const handleZoomOut = useCallback(() => {
    zoomOut({ duration: 300 });
  }, [zoomOut]);

  const handleAddLabel = useCallback(() => {
    // Use canvas menu position if available, otherwise use center
    let position;
    if (canvasMenuPosition) {
      position = screenToFlowPosition({
        x: canvasMenuPosition.x,
        y: canvasMenuPosition.y,
      });
    } else {
      // Fallback to center of viewport
      position = screenToFlowPosition({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      });
    }

    const newNode = {
      id: `label-${Date.now()}`,
      type: 'label',
      position,
      data: {
        content: '',
        color: 'yellow',
      },
      style: {
        width: 200,
        height: 150,
        zIndex: -1, // Place behind all nodes and edges
      },
    };

    addNode(newNode);
    toast.success('Label added');

    // Close canvas menu if it was open
    if (onCanvasMenuOpenChange) {
      onCanvasMenuOpenChange(false);
    }
  }, [canvasMenuPosition, screenToFlowPosition, addNode, onCanvasMenuOpenChange]);

  // Render menu items (shared between ContextMenu and DropdownMenu)
  const menuItems = (
    <>
      <ContextMenuItem onClick={handleAddLabel}>
        <Tag className="mr-2 h-4 w-4" />
        Add Label
      </ContextMenuItem>
      <ContextMenuSeparator />
      {selectedNodes.length > 0 && (
        <>
          <ContextMenuItem onClick={() => {
            copyNodes(selectedNodes);
            toast.success(`Copied ${selectedNodes.length} node(s)`);
          }}>
            <Clipboard className="mr-2 h-4 w-4" />
            Copy ({selectedNodes.length})
            <ContextMenuShortcut>{shortcuts.copy}</ContextMenuShortcut>
          </ContextMenuItem>
          <ContextMenuSeparator />
        </>
      )}
      <ContextMenuItem onClick={handlePaste} disabled={!canPaste}>
        <Clipboard className="mr-2 h-4 w-4" />
        Paste
        <ContextMenuShortcut>{shortcuts.paste}</ContextMenuShortcut>
      </ContextMenuItem>
      <ContextMenuSeparator />
      <ContextMenuItem onClick={handleSelectAll}>
        <MousePointerClick className="mr-2 h-4 w-4" />
        Select All
        <ContextMenuShortcut>{shortcuts.selectAll}</ContextMenuShortcut>
      </ContextMenuItem>
      <ContextMenuItem onClick={handleDeselectAll}>
        <MousePointerClick className="mr-2 h-4 w-4" />
        Deselect All
        <ContextMenuShortcut>{shortcuts.escape}</ContextMenuShortcut>
      </ContextMenuItem>
      {selectedNodes.length > 0 && (
        <>
          <ContextMenuSeparator />
          <ContextMenuItem onClick={handleDeleteSelected} className="text-red-600">
            <Trash2 className="mr-2 h-4 w-4" />
            Delete Selected ({selectedNodes.length})
            <ContextMenuShortcut>{shortcuts.delete}</ContextMenuShortcut>
          </ContextMenuItem>
        </>
      )}
      <ContextMenuSeparator />
      <ContextMenuItem onClick={handleZoomIn}>
        <ZoomIn className="mr-2 h-4 w-4" />
        Zoom In
        <ContextMenuShortcut>{shortcuts.zoomIn}</ContextMenuShortcut>
      </ContextMenuItem>
      <ContextMenuItem onClick={handleZoomOut}>
        <ZoomOut className="mr-2 h-4 w-4" />
        Zoom Out
        <ContextMenuShortcut>{shortcuts.zoomOut}</ContextMenuShortcut>
      </ContextMenuItem>
      <ContextMenuItem onClick={handleFitView}>
        <Maximize className="mr-2 h-4 w-4" />
        Fit View
        <ContextMenuShortcut>{shortcuts.fitView}</ContextMenuShortcut>
      </ContextMenuItem>
    </>
  );

  // Render dropdown menu items (for canvas right-click)
  const dropdownMenuItems = (
    <>
      <DropdownMenuItem onClick={handleAddLabel}>
        <Tag className="mr-2 h-4 w-4" />
        Add Label
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      {selectedNodes.length > 0 && (
        <>
          <DropdownMenuItem onClick={() => {
            copyNodes(selectedNodes);
            toast.success(`Copied ${selectedNodes.length} node(s)`);
            if (onCanvasMenuOpenChange) onCanvasMenuOpenChange(false);
          }}>
            <Clipboard className="mr-2 h-4 w-4" />
            Copy ({selectedNodes.length})
            <DropdownMenuShortcut>{shortcuts.copy}</DropdownMenuShortcut>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
        </>
      )}
      <DropdownMenuItem 
        onClick={() => {
          handlePaste();
          if (onCanvasMenuOpenChange) onCanvasMenuOpenChange(false);
        }}
        disabled={!canPaste}
      >
        <Clipboard className="mr-2 h-4 w-4" />
        Paste
        <DropdownMenuShortcut>{shortcuts.paste}</DropdownMenuShortcut>
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={() => {
        handleSelectAll();
        if (onCanvasMenuOpenChange) onCanvasMenuOpenChange(false);
      }}>
        <MousePointerClick className="mr-2 h-4 w-4" />
        Select All
        <DropdownMenuShortcut>{shortcuts.selectAll}</DropdownMenuShortcut>
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => {
        handleDeselectAll();
        if (onCanvasMenuOpenChange) onCanvasMenuOpenChange(false);
      }}>
        <MousePointerClick className="mr-2 h-4 w-4" />
        Deselect All
        <DropdownMenuShortcut>{shortcuts.escape}</DropdownMenuShortcut>
      </DropdownMenuItem>
      {selectedNodes.length > 0 && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem 
            onClick={() => {
              handleDeleteSelected();
              if (onCanvasMenuOpenChange) onCanvasMenuOpenChange(false);
            }} 
            className="text-red-600"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete Selected ({selectedNodes.length})
            <DropdownMenuShortcut>{shortcuts.delete}</DropdownMenuShortcut>
          </DropdownMenuItem>
        </>
      )}
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={() => {
        handleZoomIn();
        if (onCanvasMenuOpenChange) onCanvasMenuOpenChange(false);
      }}>
        <ZoomIn className="mr-2 h-4 w-4" />
        Zoom In
        <DropdownMenuShortcut>{shortcuts.zoomIn}</DropdownMenuShortcut>
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => {
        handleZoomOut();
        if (onCanvasMenuOpenChange) onCanvasMenuOpenChange(false);
      }}>
        <ZoomOut className="mr-2 h-4 w-4" />
        Zoom Out
        <DropdownMenuShortcut>{shortcuts.zoomOut}</DropdownMenuShortcut>
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => {
        handleFitView();
        if (onCanvasMenuOpenChange) onCanvasMenuOpenChange(false);
      }}>
        <Maximize className="mr-2 h-4 w-4" />
        Fit View
        <DropdownMenuShortcut>{shortcuts.fitView}</DropdownMenuShortcut>
      </DropdownMenuItem>
    </>
  );

  return (
    <>
      {/* Canvas dropdown menu (triggered by onPaneContextMenu) */}
      <DropdownMenu open={canvasMenuOpen} onOpenChange={onCanvasMenuOpenChange}>
        <DropdownMenuContent 
          className="w-56"
          style={canvasMenuPosition ? {
            position: 'fixed',
            left: `${canvasMenuPosition.x}px`,
            top: `${canvasMenuPosition.y}px`,
          } : undefined}
          onContextMenu={(e) => e.preventDefault()}
        >
          {dropdownMenuItems}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Node/Edge context menu (triggered by right-click on nodes/edges) */}
      <ContextMenu>
        <ContextMenuTrigger asChild>
          {children}
        </ContextMenuTrigger>
        <ContextMenuContent className="w-56">
          {menuItems}
        </ContextMenuContent>
      </ContextMenu>
    </>
  );
}
